import { CheckCircle2, Circle, XCircle } from "lucide-react";

// 9-stage pipeline. Each stage's timestamp field indicates completion;
// approval stages also track an explicit status ("approved"|"rejected"|null).
export const PIPELINE_STAGES = [
  { key: "verified",       label: "Verified by Office Staff", tsField: "verified_at" },
  { key: "in_bank_pool",   label: "Sent to Bank Pool",        tsField: "bank_pool_at" },
  { key: "taken",          label: "Taken by Bank",            tsField: "taken_by_bank_at" },
  { key: "cibil",          label: "CIBIL Approval",           tsField: "cibil_at", approvalKey: "cibil" },
  { key: "legal",          label: "Legal Approval",           tsField: "legal_at", approvalKey: "legal" },
  { key: "tech",           label: "Tech Approval",            tsField: "tech_at",  approvalKey: "tech"  },
  { key: "ok",             label: "Bank Approval (OK)",       tsField: "bank_approval_at" },
  { key: "paid",           label: "Consulting Fees Paid",     tsField: "fees_at" },
  { key: "disbursed",      label: "Loan Disbursed",           tsField: "disbursed_at" },
];

export const fmtTs = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

function stageState(lead, stage) {
  if (stage.approvalKey) {
    const s = lead[`${stage.approvalKey}_status`];
    if (s === "approved") return "done";
    if (s === "rejected") return "rejected";
    return lead[stage.tsField] ? "done" : "pending";
  }
  return lead[stage.tsField] ? "done" : "pending";
}

/**
 * Read-only 9-stage timeline. Same source of truth used everywhere.
 * @param lead   Full lead doc (needs cibil_status/at, legal_*, tech_*, verified_at,
 *               bank_pool_at, taken_by_bank_at, bank_approval_at, fees_at, disbursed_at)
 * @param compact  If true, renders a tighter version (used inside modals)
 */
export default function PipelineTimeline({ lead, compact = false }) {
  if (!lead) return null;
  const states = PIPELINE_STAGES.map(s => stageState(lead, s));
  const lastDone = states.lastIndexOf("done");
  const dotSize = compact ? "w-4 h-4" : "w-5 h-5";
  const iconSize = compact ? "w-2.5 h-2.5" : "w-3 h-3";
  const lineHeight = compact ? "h-6" : "h-8";
  return (
    <div className={compact ? "space-y-2.5" : "space-y-4"} data-testid={`pipeline-timeline-${lead.lead_id}`}>
      {PIPELINE_STAGES.map((s, i) => {
        const state = states[i];
        const done = state === "done";
        const rejected = state === "rejected";
        const current = i === lastDone + 1 && !rejected;
        const ts = lead[s.tsField];
        const remarks = s.approvalKey ? lead[`${s.approvalKey}_remarks`] : "";
        return (
          <div key={s.key} className="flex items-start gap-3" data-testid={`pt-stage-${lead.lead_id}-${s.key}`}>
            <div className="flex flex-col items-center">
              {rejected ? (
                <div className={`${dotSize} rounded-full bg-red-500 flex items-center justify-center`}>
                  <XCircle className={`${iconSize} text-slate-900`}/>
                </div>
              ) : done ? (
                <div className={`${dotSize} rounded-full bg-amber-500 ${current ? "gold-glow pulse-gold" : ""} flex items-center justify-center`}>
                  <CheckCircle2 className={`${iconSize} text-slate-900`}/>
                </div>
              ) : (
                <Circle className={`${dotSize} text-slate-700`}/>
              )}
              {i < PIPELINE_STAGES.length - 1 && (
                <div className={`w-0.5 ${lineHeight} mt-1 ${done ? "bg-amber-500" : rejected ? "bg-red-500" : "bg-slate-800"}`}/>
              )}
            </div>
            <div className={compact ? "pb-1.5 min-w-0" : "pb-4 min-w-0"}>
              <div className={`${compact ? "text-sm" : ""} font-heading ${rejected ? "text-red-300" : done ? "text-white" : "text-slate-500"}`}>
                {s.label}
                {rejected && <span className="ml-2 text-[10px] uppercase tracking-widest text-red-400">Rejected</span>}
              </div>
              {(done || rejected) && ts && (
                <div className={`text-[11px] mt-0.5 font-mono-alt ${rejected ? "text-red-400" : "text-slate-400"}`}
                     data-testid={`pt-ts-${s.key}`}>{fmtTs(ts)}</div>
              )}
              {remarks && (done || rejected) && (
                <div className="text-xs text-slate-400 italic mt-0.5 break-words">"{remarks}"</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
